"use client";

import { useId } from "react";
import { ordinal } from "./percentile";

interface PercentileCurveProps {
  /** The computed percentile, or null before the user enters a score. */
  percentile: number | null;
  /** Tailwind fill utility for the marker pill. */
  markerFillClassName?: string;
  /** Tailwind stroke utility for the marker line at the user's score. */
  markerStrokeClassName?: string;
}

const WIDTH = 1000;
const HEIGHT = 170;
const BASELINE = 140;

// Decorative, right-skewed density curve — the shape itself carries no
// statistical meaning, only the marker's x-position (the real computed
// percentile) does. Skewed (steeper rise, longer tail) to match the
// in-product reference visual rather than a symmetric bell.
const CURVE_PATH = `M0,${BASELINE}
  C90,${BASELINE - 3} 160,${BASELINE - 19} 230,${BASELINE - 51}
  C290,${BASELINE - 78} 340,${BASELINE - 111} 420,${BASELINE - 124}
  C480,${BASELINE - 124} 540,${BASELINE - 102} 620,${BASELINE - 70}
  C720,${BASELINE - 35} 830,${BASELINE - 13} 1000,${BASELINE}
  L1000,${BASELINE} L0,${BASELINE} Z`;

const GAP_WIDTH = 6;
const QUARTILES = [25, 50, 75];

export function PercentileCurve({
  percentile,
  markerFillClassName = "fill-fin",
  markerStrokeClassName = "stroke-fin",
}: PercentileCurveProps) {
  const maskId = useId();
  const clamped = Math.min(100, Math.max(0, percentile ?? 50));
  const markerX = (clamped / 100) * WIDTH;

  return (
    <div className="relative w-full pt-6">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="aspect-[1000/230] w-full overflow-visible sm:aspect-[1000/170]"
        role="img"
        aria-label={
          percentile === null
            ? "Enter your CSAT score to see your percentile"
            : `You are in the ${ordinal(clamped)} percentile`
        }
      >
        <mask id={maskId}>
          <rect x={0} y={0} width={WIDTH} height={HEIGHT} fill="white" />
          {QUARTILES.map((tick) => (
            <rect
              key={tick}
              x={(tick / 100) * WIDTH - GAP_WIDTH / 2}
              y={0}
              width={GAP_WIDTH}
              height={BASELINE}
              fill="black"
            />
          ))}
        </mask>

        <path
          d={CURVE_PATH}
          className="fill-current/10"
          mask={`url(#${maskId})`}
        />

        {percentile !== null && (
          <line
            x1={markerX}
            x2={markerX}
            y1={0}
            y2={BASELINE}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            className={markerStrokeClassName}
          />
        )}
      </svg>

      {/* Pin head overlaid as a fixed-size element: the curve SVG uses
          preserveAspectRatio="none" so anything drawn inside it squashes with
          the container width. Keeping the marker out here holds its size. */}
      {percentile !== null && (
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-0"
          style={{ left: `${clamped}%` }}
        >
          <svg
            width={60}
            height={26}
            viewBox="0 0 60 26"
            className="-translate-x-1/2 overflow-visible"
          >
            <rect
              x={0}
              y={0}
              width={60}
              height={26}
              rx={13}
              className={markerFillClassName}
            />
            <text
              x={30}
              y={17.5}
              textAnchor="middle"
              className="fill-white text-[14px] font-semibold"
            >
              {ordinal(clamped)}
            </text>
          </svg>
        </div>
      )}

      <div className="relative mt-1.5 flex justify-between">
        {["0th", "25th", "50th", "75th", "100th"].map((label) => (
          <span key={label} className="text-body-sm text-current/60">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
